import { useState } from 'react';
import { AlertTriangle, Trash2, PlayCircle, Eye, Plus, Edit2 } from 'lucide-react';
import type { CrisisScenario } from '../../hooks/useCrisisScenarios';

interface CrisisScenariosListProps {
  scenarios: CrisisScenario[];
  activeScenarioId: string | null;
  onSelectScenario: (scenarioId: string | null) => void;
  onDeleteScenario: (scenarioId: string) => void;
  onCreateScenario: () => void;
}

export function CrisisScenariosList({
  scenarios,
  activeScenarioId,
  onSelectScenario,
  onDeleteScenario,
  onCreateScenario
}: CrisisScenariosListProps) {
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleDelete = (scenarioId: string) => {
    if (confirmDelete === scenarioId) {
      onDeleteScenario(scenarioId);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(scenarioId);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Scénarios de crise</h3>
        <button
          onClick={onCreateScenario}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Créer un Crash Test</span>
        </button>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => onSelectScenario(null)}
          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
            activeScenarioId === null
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <PlayCircle className={`h-5 w-5 ${
                activeScenarioId === null ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <div>
                <div className="font-medium text-gray-900">Situation de base</div>
                <div className="text-sm text-gray-500">Plan de trésorerie réel</div>
              </div>
            </div>
          </div>
        </button>

        {scenarios.length === 0 && (
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">Aucun scénario de crise créé</p>
            <p className="text-sm text-gray-400">
              Créez votre premier crash test pour simuler des événements critiques
            </p>
          </div>
        )}

        {scenarios.map(scenario => (
          <div
            key={scenario.id}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeScenarioId === scenario.id
                ? 'border-red-600 bg-red-50'
                : 'border-gray-200 hover:border-red-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <button
                onClick={() => onSelectScenario(scenario.id)}
                className="flex-1 text-left"
              >
                <div className="flex items-center space-x-3">
                  <AlertTriangle className={`h-5 w-5 ${
                    activeScenarioId === scenario.id ? 'text-red-600' : 'text-gray-400'
                  }`} />
                  <div>
                    <div className="font-medium text-gray-900">{scenario.name}</div>
                    {scenario.description && (
                      <div className="text-sm text-gray-500 mt-1">{scenario.description}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      Créé le {new Date(scenario.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
              </button>

              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleDelete(scenario.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    confirmDelete === scenario.id
                      ? 'bg-red-600 text-white'
                      : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                  }`}
                  title={confirmDelete === scenario.id ? 'Confirmer la suppression' : 'Supprimer'}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
